import React from 'react';
import { map } from 'lodash';
import { connect } from 'react-redux';

import {
    DISPLAY_STEP,
    STATE_PENDING,
    STATE_VALID,
    STATE_INVALID
} from '../../../../../constants/payment';
import {
    PURCHASE_TYPE_ONLINE_CASHBACK,
    PURCHASE_TYPE_OFFLINE_CASHBACK,
    PURCHASE_TYPE_EBON_CASHBACK,
    PURCHASE_TYPE_REMAINING_POOL,
    PURCHASE_TYPE_CUSTOMER_SERVICE_REGULARIZATION,
    PURCHASE_TYPE_GIVEAWAY_BONUS
} from '../../../../../constants/purchase';
import { STATIC_ROOT } from '../../../../../constants';
import { MOMENTJS_DATE_FORMAT_STANDARD } from '../../../../../constants/moment';

import { fetchAccountPurchasesIfNeeded } from '../../../../../services/account/cashback/actions';

import { toDate } from '../../../../../utils/dateUtils';
import { formatAmountCurrency } from '../../../../../utils/index';
import { convertHtmlToReactDomWithParams } from '../../../../../utils/htmlUtils';

import SvgComponent from '../../../../SvgComponent';
import { LoadingComponent } from '../../../../loading/LoadingComponent';
import CashbackHistoryLegendComponent from './CashbackHistoryLegendComponent';

import './CashbackHistoryComponent.scss';
import ButtonComponent from '../../../../button/ButtonComponent';

const PURCHASE_STATE_PICTO_SIZE = 20;

// Libellé du type d'achat (clé i18n dans CASHBACK_JACKPOT.PURCHASE_TYPES) ;
// un type absent de cette table n'affiche pas de libellé
const PURCHASE_TYPE_I18N_KEYS = {
    [PURCHASE_TYPE_ONLINE_CASHBACK]: 'ONLINE_CASHBACK',
    [PURCHASE_TYPE_OFFLINE_CASHBACK]: 'OFFLINE_CASHBACK',
    [PURCHASE_TYPE_EBON_CASHBACK]: 'EBON',
    [PURCHASE_TYPE_CUSTOMER_SERVICE_REGULARIZATION]: 'CUSTOMER_SERVICE_REGULARIZATION',
    [PURCHASE_TYPE_GIVEAWAY_BONUS]: 'GIVEAWAY_BONUS'
};

// Pictos des états : les états standards vivent dans /icon ; la remise immédiate
// sur eBon réutilise le picto eBon des blocs marchands
const stateIconSrc = icon => `${STATIC_ROOT}/icon/picto-${icon}.svg`;
const EBON_PICTO_SRC = `${STATIC_ROOT}/picto-ebon.svg`;

const renderPurchaseState = (src, text) => (
    <React.Fragment>
        <span>
            <SvgComponent
                src={src}
                width={PURCHASE_STATE_PICTO_SIZE}
                height={PURCHASE_STATE_PICTO_SIZE}
                aria-hidden="true"
                focusable="false"
            />
        </span>
        <span>{text}</span>
    </React.Fragment>
);

class CashbackHistoryComponent extends React.Component {
    constructor(props) {
        super(props);
        this.state = { index: DISPLAY_STEP };
    }

    componentDidMount() {
        this.props.fetchAccountPurchasesIfNeeded();
        this.scrollTop();
    }

    scrollTop = () => {
        if (document.getElementById('purchases')) {
            document.getElementById('purchases').scrollIntoView({ behavior: 'smooth' });
        }
    };

    renderConditionnalOnPurchaseState(purchase) {
        const { state: purchaseState, paymentDate, type } = purchase;
        if (!purchaseState) return null;

        const i18n = this.props.i18n;

        if (purchaseState === STATE_VALID && paymentDate > 0) {
            // Achat eBon : la remise a été déduite du montant de la commande,
            // il n'y a pas eu de versement à proprement parler
            if (type === PURCHASE_TYPE_EBON_CASHBACK) {
                return renderPurchaseState(EBON_PICTO_SRC, i18n.EBON_DISCOUNT);
            }
            return renderPurchaseState(stateIconSrc('check'), i18n.PAID);
        } else if (purchaseState === STATE_VALID) {
            return renderPurchaseState(stateIconSrc('validation'), i18n.VALID);
        } else if (purchaseState === STATE_PENDING) {
            return renderPurchaseState(stateIconSrc('pending'), i18n.WAITING);
        } else if (purchaseState === STATE_INVALID) {
            return renderPurchaseState(stateIconSrc('invalide'), i18n.INVALID);
        }
    }

    renderSeeMoreButton(filtered) {
        const { i18n } = this.props;
        const { index } = this.state;

        if (filtered.length >= DISPLAY_STEP && index < filtered.length) {
            return (
                <div className="purchase-button">
                    <ButtonComponent
                        id="more-purchase-button"
                        cssClass="see-more-button"
                        color="main"
                        text={
                            <React.Fragment>
                                <span className="plus" aria-hidden="true">
                                    +
                                </span>
                                <span className="text">{i18n.SEE_MORE_OPERATION}</span>
                            </React.Fragment>
                        }
                        onClick={this.handleSeeMoreClick}
                    />
                </div>
            );
        }
    }

    handleSeeMoreClick = () => {
        this.setState({
            index: this.state.index + DISPLAY_STEP
        });
    };

    getPartnerName = purchase => {
        if (purchase.type === PURCHASE_TYPE_GIVEAWAY_BONUS) {
            return purchase.label;
        }
        return purchase.partnerName;
    };

    renderPurchaseType(type) {
        const key = PURCHASE_TYPE_I18N_KEYS[type];
        const label = key && this.props.i18n.PURCHASE_TYPES[key];

        if (!label) return null;

        return <span className="purchase-type">{label}</span>;
    }

    render() {
        const { data, fetching, i18n } = this.props;
        const { index } = this.state;

        const safeData = Array.isArray(data) ? data : [];
        const filtered = safeData.filter(p => p.type !== PURCHASE_TYPE_REMAINING_POOL);
        const visiblePurchases = filtered.slice(0, index);

        return (
            <div id="purchases">
                <h3>{i18n.PURCHASE_HISTORY}</h3>
                <div className="information-div" role="note">
                    <span aria-hidden="true">
                        <SvgComponent
                            src={`${STATIC_ROOT}/information.svg`}
                            width="20"
                            height="20"
                            aria-hidden="true"
                            focusable="false"
                        />
                    </span>
                    <span>{convertHtmlToReactDomWithParams(i18n.GAIN_CASHBACK_HISTORIC)}</span>
                </div>

                {!fetching && visiblePurchases.length > 0 && (
                    <table className="purchases-table" aria-label={i18n.TABLE_LABEL}>
                        <thead>
                            <tr>
                                <th scope="col">{i18n.COLUMN_DATE}</th>
                                <th scope="col">{i18n.COLUMN_PARTNER}</th>
                                <th scope="col">{i18n.COLUMN_AMOUNT}</th>
                                <th scope="col">{i18n.COLUMN_STATE}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {map(visiblePurchases, (purchase, i) => (
                                <tr className="container-purchase" key={i}>
                                    <td>
                                        {toDate(
                                            purchase.purchaseDate,
                                            MOMENTJS_DATE_FORMAT_STANDARD
                                        )}
                                    </td>
                                    <td>
                                        {this.renderPurchaseType(purchase.type)}
                                        {this.getPartnerName(purchase)}
                                    </td>
                                    <td>{formatAmountCurrency(purchase.gain)}</td>
                                    <td>{this.renderConditionnalOnPurchaseState(purchase)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {(fetching || safeData.length === 0) && (
                    <div
                        className={`container-purchase empty ${fetching ? 'fetching' : ''}`}
                        role="status"
                        aria-live="polite">
                        {fetching ? <LoadingComponent /> : i18n.NO_PURCHASE}
                    </div>
                )}

                {this.renderSeeMoreButton(filtered)}

                <CashbackHistoryLegendComponent />
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        i18n: state.i18n.page.ACCOUNT.CASHBACK_JACKPOT,
        fetching: state.account.mycashback.fetching,
        data: state.account.mycashback.purchases || []
    };
}

const mapDispatchToProps = {
    fetchAccountPurchasesIfNeeded
};

export default connect(mapStateToProps, mapDispatchToProps)(CashbackHistoryComponent);



// WEBPACK FOOTER //
// ./src/components/pages/account/cashback/history/CashbackHistoryComponent.js